import { ReactElement, useMemo, useState } from 'react'
import { Button, FormControl, MenuItem, Select, Stack, Typography } from '@mui/material'
import type {
  AlignmentConfig,
  GlbFileAdditionalData,
} from '@editor/components/Controls/IFCUploadWidget/types/ifcMetadata'

interface Props {
  /** IFC files (other than the active one) that have a saved alignment config. */
  sourceFiles: FileItem[]
  /** Applies the picked file's alignment config to the active file. */
  onCopy: (config: AlignmentConfig) => void
  /** Resets the active file's alignment config to the default (zero) alignment. */
  onReset: () => void
}

/**
 * "Kopieren von" tab of the alignment section: pick another IFC model that already has an
 * alignment config and copy that config onto the active model.
 */
const CopyAlignmentTab = ({ sourceFiles, onCopy, onReset }: Props): ReactElement => {
  const [sourceGuid, setSourceGuid] = useState('')

  // The picked file can drop out of the options (e.g. it became the active file) — treat that as
  // "nothing selected" instead of keeping a stale value.
  const selectedFile = useMemo(
    () => sourceFiles.find(file => file.guid === sourceGuid) ?? null,
    [sourceFiles, sourceGuid],
  )
  const sourceConfig = (selectedFile?.additional_data as GlbFileAdditionalData | undefined)
    ?.alignment_config

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Übernehmen Sie die Ausrichtung eines anderen IFC-Modells für das aktive Modell.
      </Typography>

      <FormControl size="small" variant="outlined" fullWidth>
        <Select
          variant="outlined"
          size="small"
          value={selectedFile?.guid ?? ''}
          onChange={event => setSourceGuid(event.target.value)}
          displayEmpty
          renderValue={value => {
            if (!value) {
              return (
                <Typography component="span" color="text.secondary">
                  IFC-Modell auswählen
                </Typography>
              )
            }
            return sourceFiles.find(file => file.guid === value)?.name ?? ''
          }}
          data-cy="select-ifc-copy-alignment-source"
        >
          {sourceFiles.map(file => (
            <MenuItem key={file.guid} value={file.guid}>
              {file.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <Stack direction="row" spacing={1}>
        <Button
          variant="contained"
          size="small"
          disabled={!sourceConfig}
          onClick={() => sourceConfig && onCopy(sourceConfig)}
          data-cy="btn-ifc-copy-alignment"
        >
          Ausrichtung kopieren
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={onReset}
          data-cy="btn-ifc-copy-reset-alignment"
        >
          Zurücksetzen
        </Button>
      </Stack>
    </Stack>
  )
}

export default CopyAlignmentTab
